import React, { useState } from "react";
import { Link } from "react-router-dom";
import logo from "../assets/img/logo.jpeg";
import { useNavigate } from "react-router-dom";
import {
  FiBell,
  FiSettings,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiAlertTriangle,
  FiX,
} from "react-icons/fi";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');

  :root {
    --tb-teal:    #0a5556;
    --tb-mid:     #0c7172;
    --tb-lite:    #14b8a6;
    --tb-gold:    #FFD166;
    --tb-bg:      #ffffff;
    --tb-surface: #f3fafa;
    --tb-border:  rgba(10,85,86,0.13);
    --tb-text:    #062020;
    --tb-muted:   #6b8a8a;
    --tb-danger:  #ef4444;
    --tb-shadow:  0 4px 24px rgba(10,85,86,0.10);
  }

  @keyframes tb__fadeIn    { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes tb__shimmer   { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
  @keyframes tb__pulse     { 0%,100%{transform:scale(1)} 50%{transform:scale(1.15)} }
  @keyframes tb__modalIn   { from{opacity:0;transform:scale(0.94) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }
  @keyframes tb__overlayIn { from{opacity:0} to{opacity:1} }

  /* ── shell ── */
  .tb {
    height: 62px;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(20px);
    padding: 0 24px 0 283px;
    display: flex; align-items: center; justify-content: space-between;
    box-shadow: 0 1px 0 var(--tb-border), var(--tb-shadow);
    font-family: 'DM Sans', sans-serif;
    position: sticky; top: 0; z-index: 999;
    border-bottom: 1px solid var(--tb-border);
  }
  .tb::after {
    content: '';
    position: absolute; bottom: 0; left: 0; right: 0; height: 2px;
    background: linear-gradient(90deg, var(--tb-teal) 0%, var(--tb-lite) 55%, var(--tb-gold) 100%);
  }

  /* logo */
  .tb__logo { display: flex; align-items: center; gap: 11px; text-decoration: none; }
  .tb__logo-img { height: 38px; width: auto; border-radius: 10px; object-fit: cover; display: block; box-shadow: 0 4px 12px rgba(10,85,86,0.20); }
  .tb__logo-text { display: flex; flex-direction: column; }
  .tb__logo-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 800; color: var(--tb-teal); letter-spacing: -0.03em; line-height: 1; }
  .tb__logo-tag  { font-size: 9.5px; color: var(--tb-muted); font-weight: 500; letter-spacing: 0.04em; margin-top: 2px; }

  /* right group */
  .tb__right { display: flex; align-items: center; gap: 8px; }

  /* icon button */
  .tb__icon-btn {
    width: 38px; height: 38px; border-radius: 10px;
    border: 1.5px solid var(--tb-border);
    background: var(--tb-surface);
    display: flex; align-items: center; justify-content: center;
    color: var(--tb-muted); font-size: 17px; cursor: pointer;
    position: relative;
    transition: all 0.2s;
  }
  .tb__icon-btn:hover { background: var(--tb-teal); border-color: var(--tb-teal); color: #fff; box-shadow: 0 4px 14px rgba(10,85,86,0.30); }

  /* notification dot */
  .tb__notif-dot {
    position: absolute; top: 7px; right: 7px;
    width: 7px; height: 7px; border-radius: 50%;
    background: var(--tb-danger);
    border: 1.5px solid #fff;
    animation: tb__pulse 2s infinite;
  }

  /* dropdown */
  .tb__dropdown { position: relative; }
  .tb__dropdown-menu {
    position: absolute; top: calc(100% + 10px); right: 0;
    background: #fff;
    border: 1px solid var(--tb-border);
    border-radius: 14px;
    box-shadow: 0 16px 48px rgba(10,85,86,0.14), 0 4px 12px rgba(0,0,0,0.06);
    opacity: 0; visibility: hidden; pointer-events: none;
    transform: translateY(6px);
    transition: all 0.22s cubic-bezier(0.4,0,0.2,1);
    z-index: 100; overflow: hidden;
    min-width: 200px;
  }
  .tb__dropdown:hover .tb__dropdown-menu,
  .tb__dropdown--open .tb__dropdown-menu {
    opacity: 1; visibility: visible; pointer-events: all; transform: translateY(0);
  }

  /* notification panel */
  .tb__notif-panel { width: 290px; }
  .tb__notif-head {
    padding: 14px 16px 10px;
    display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid rgba(10,85,86,0.08);
  }
  .tb__notif-title { font-family: 'Sora', sans-serif; font-size: 12px; font-weight: 700; color: var(--tb-teal); letter-spacing: 0.06em; text-transform: uppercase; }
  .tb__notif-count { font-size: 10px; background: rgba(10,85,86,0.09); color: var(--tb-teal); font-weight: 700; padding: 2px 8px; border-radius: 50px; }
  .tb__notif-empty { padding: 28px 16px; text-align: center; }
  .tb__notif-empty-icon { font-size: 28px; margin-bottom: 8px; opacity: 0.35; }
  .tb__notif-empty-text { font-size: 12.5px; color: var(--tb-muted); }

  /* divider */
  .tb__divider { height: 1px; background: rgba(10,85,86,0.07); margin: 4px 0; }

  /* profile trigger */
  .tb__profile {
    display: flex; align-items: center; gap: 9px;
    padding: 5px 12px 5px 6px;
    background: var(--tb-surface);
    border: 1.5px solid var(--tb-border);
    border-radius: 50px; cursor: pointer;
    transition: all 0.2s;
  }
  .tb__profile:hover { border-color: var(--tb-teal); background: rgba(10,85,86,0.06); box-shadow: 0 4px 14px rgba(10,85,86,0.12); }
  .tb__profile-avatar {
    width: 30px; height: 30px; border-radius: 50%;
    background: linear-gradient(135deg, var(--tb-teal), var(--tb-lite));
    display: flex; align-items: center; justify-content: center;
    font-family: 'Sora', sans-serif; font-size: 11px; font-weight: 700; color: #fff;
    flex-shrink: 0;
    box-shadow: 0 3px 10px rgba(10,85,86,0.30);
  }
  .tb__profile-info { display: flex; flex-direction: column; }
  .tb__profile-name { font-size: 12.5px; font-weight: 700; color: var(--tb-teal); line-height: 1; }
  .tb__profile-role { font-size: 10px; color: var(--tb-muted); margin-top: 2px; }
  .tb__profile-arrow { color: var(--tb-muted); font-size: 13px; transition: transform 0.2s; }
  .tb__dropdown:hover .tb__profile-arrow { transform: rotate(180deg); color: var(--tb-teal); }

  /* dropdown header */
  .tb__dd-user {
    padding: 14px 16px 12px;
    border-bottom: 1px solid rgba(10,85,86,0.08);
    background: linear-gradient(135deg, rgba(10,85,86,0.04), rgba(20,184,166,0.02));
  }
  .tb__dd-avatar {
    width: 40px; height: 40px; border-radius: 12px;
    background: linear-gradient(135deg, var(--tb-teal), var(--tb-lite));
    display: flex; align-items: center; justify-content: center;
    font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; color: #fff;
    box-shadow: 0 4px 14px rgba(10,85,86,0.30);
    margin-bottom: 9px;
  }
  .tb__dd-name { font-family: 'Sora', sans-serif; font-size: 14px; font-weight: 700; color: var(--tb-text); line-height: 1; }
  .tb__dd-email { font-size: 11px; color: var(--tb-muted); margin-top: 3px; }

  /* dropdown items */
  .tb__dd-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px; font-size: 13px; font-weight: 500;
    color: #2d4a4a; text-decoration: none;
    transition: all 0.16s; cursor: pointer;
    border: none; background: transparent; width: 100%; text-align: left;
    font-family: 'DM Sans', sans-serif;
  }
  .tb__dd-item:hover { background: rgba(10,85,86,0.07); color: var(--tb-teal); text-decoration: none; }
  .tb__dd-item svg { font-size: 15px; opacity: 0.7; }
  .tb__dd-item--logout { color: var(--tb-danger); }
  .tb__dd-item--logout:hover { background: rgba(239,68,68,0.08); color: var(--tb-danger); }
  .tb__dd-item--logout svg { color: var(--tb-danger); }

  /* ── CUSTOM LOGOUT MODAL ── */
  .tb__overlay {
    position: fixed; inset: 0; z-index: 9998;
    background: rgba(6,32,32,0.55);
    backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center;
    animation: tb__overlayIn 0.2s ease;
  }
  .tb__modal {
    background: #fff;
    border-radius: 24px;
    padding: 0;
    width: 100%; max-width: 390px; margin: 16px;
    box-shadow: 0 32px 80px rgba(10,85,86,0.22), 0 8px 24px rgba(0,0,0,0.12);
    animation: tb__modalIn 0.28s cubic-bezier(0.34,1.56,0.64,1);
    overflow: hidden;
    position: relative;
  }
  .tb__modal::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, var(--tb-danger), #f87171);
  }
  .tb__modal-body {
    padding: 32px 28px 24px;
    display: flex; flex-direction: column; align-items: center; text-align: center;
  }
  .tb__modal-icon {
    width: 62px; height: 62px; border-radius: 18px; margin-bottom: 18px;
    background: rgba(239,68,68,0.10);
    border: 1.5px solid rgba(239,68,68,0.20);
    display: flex; align-items: center; justify-content: center;
    font-size: 26px; color: var(--tb-danger);
  }
  .tb__modal-title { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 800; color: var(--tb-text); letter-spacing: -0.03em; margin-bottom: 8px; }
  .tb__modal-sub   { font-size: 13.5px; color: var(--tb-muted); line-height: 1.65; max-width: 280px; }
  .tb__modal-footer {
    display: flex; gap: 10px;
    padding: 0 28px 26px;
  }
  .tb__modal-cancel {
    flex: 1; padding: 12px;
    border: 1.5px solid var(--tb-border); border-radius: 12px;
    background: var(--tb-surface); color: #2d4a4a;
    font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 700;
    cursor: pointer; transition: all 0.18s;
  }
  .tb__modal-cancel:hover { border-color: var(--tb-teal); color: var(--tb-teal); background: rgba(10,85,86,0.06); }
  .tb__modal-confirm {
    flex: 1; padding: 12px;
    border: none; border-radius: 12px;
    background: linear-gradient(135deg, #dc2626, #ef4444);
    color: #fff;
    font-family: 'Sora', sans-serif; font-size: 13.5px; font-weight: 700;
    cursor: pointer; transition: all 0.18s;
    box-shadow: 0 6px 20px rgba(239,68,68,0.35);
  }
  .tb__modal-confirm:hover { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(239,68,68,0.44); }
  .tb__modal-confirm:active { transform: translateY(0); }
  .tb__modal-close {
    position: absolute; top: 14px; right: 14px;
    width: 30px; height: 30px; border-radius: 8px;
    border: none; background: rgba(10,85,86,0.07);
    color: var(--tb-muted); font-size: 16px; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.18s;
  }
  .tb__modal-close:hover { background: rgba(239,68,68,0.10); color: var(--tb-danger); }

  @media (max-width: 600px) {
    .tb { padding: 0 14px; }
    .tb__logo-text { display: none; }
    .tb__profile-info { display: none; }
    .tb__profile-arrow { display: none; }
    .tb__profile { padding: 5px; border-radius: 50%; }
  }
`;

function Topbar() {
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const handleLogout = (e) => {
    e.preventDefault();
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem("auth_token");
    setShowLogoutModal(false);
    navigate("/login");
  };

  const cancelLogout = () => setShowLogoutModal(false);

  return (
    <>
      <style>{CSS}</style>

      {/* ── CUSTOM LOGOUT CONFIRM MODAL ── */}
      {showLogoutModal && (
        <div className="tb__overlay" onClick={(e) => e.target === e.currentTarget && cancelLogout()}>
          <div className="tb__modal">
            <button className="tb__modal-close" onClick={cancelLogout}><FiX size={15} /></button>
            <div className="tb__modal-body">
              <div className="tb__modal-icon"><FiLogOut /></div>
              <div className="tb__modal-title">Sign Out?</div>
              <div className="tb__modal-sub">
                You'll be signed out of your account. You'll need to log in again to access your dashboard.
              </div>
            </div>
            <div className="tb__modal-footer">
              <button className="tb__modal-cancel" onClick={cancelLogout}>Stay Signed In</button>
              <button className="tb__modal-confirm" onClick={confirmLogout}>Yes, Sign Out</button>
            </div>
          </div>
        </div>
      )}

      <header className="tb">

        {/* Logo */}
        <Link to="/dashboard" className="tb__logo">
          {/* <img src={logo} alt="Logo" className="tb__logo-img" /> */}
          <div className="tb__logo-text">
            <span className="tb__logo-name">Invoice ERP</span>
            <span className="tb__logo-tag">Accounting &amp; Billing</span>
          </div>
        </Link>

        {/* Right actions */}
        <div className="tb__right">

          {/* Notification bell */}
          <div className="tb__dropdown">
            <button className="tb__icon-btn">
              <FiBell size={17} />
              {/* Uncomment when there are notifications: */}
              {/* <span className="tb__notif-dot" /> */}
            </button>
            <div className="tb__dropdown-menu tb__notif-panel">
              <div className="tb__notif-head">
                <span className="tb__notif-title">Notifications</span>
                <span className="tb__notif-count">0 new</span>
              </div>
              <div className="tb__notif-empty">
                <div className="tb__notif-empty-icon">🔔</div>
                <div className="tb__notif-empty-text">No notifications yet</div>
              </div>
            </div>
          </div>

          {/* Settings shortcut */}
          <Link to="/settings" className="tb__icon-btn" style={{ textDecoration: "none" }}>
            <FiSettings size={16} />
          </Link>

          {/* Profile dropdown */}
          <div className="tb__dropdown">
            <div className="tb__profile">
              <div className="tb__profile-avatar">A</div>
              <div className="tb__profile-info">
                <span className="tb__profile-name">Admin</span>
                <span className="tb__profile-role">Super Admin</span>
              </div>
              <FiChevronDown className="tb__profile-arrow" size={13} />
            </div>

            <div className="tb__dropdown-menu" style={{ minWidth: 220 }}>
              {/* User info header */}
              <div className="tb__dd-user">
                <div className="tb__dd-avatar">A</div>
                <div className="tb__dd-name">Admin User</div>
                <div className="tb__dd-email">admin@company.com</div>
              </div>

              <div style={{ padding: "4px 0" }}>
                <Link to="/profile" className="tb__dd-item">
                  <FiUser size={14} /> My Profile
                </Link>
                <Link to="/settings" className="tb__dd-item">
                  <FiSettings size={14} /> Settings
                </Link>

                <Link to="/opt-features"  className="tb__dd-item">
                  <FiSettings size={14} /> Features List
                </Link>


                


              </div>

              <div className="tb__divider" />

              <div style={{ padding: "4px 0 8px" }}>
                <button className="tb__dd-item tb__dd-item--logout" onClick={handleLogout}>
                  <FiLogOut size={14} /> Sign Out
                </button>
              </div>
            </div>
          </div>

        </div>
      </header>
    </>
  );
}

export default Topbar;
